import { getDXPEnvironmentRootPath } from '@/utils/envUtils'
import Response from '@/modules/core/models/Response'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import {
  createHttpMeta,
  trackHttpResponse,
  trackHttpError
} from '@/config/insight/tracking-http.insight'

export interface RequestConfig<D = any> {
  url?: string
  method?: string
  baseURL?: string
  headers?: any
  params?: any
  data?: D
}

function clearCache(path: string): string {
  const env = import.meta.env
  if (
    !env.PROD ||
    env.MODE !== 'production' ||
    (env['VITE_APP_ENV'] && env['VITE_APP_ENV'] !== 'PRODUCCIÓN')
  ) {
    return path
  }
  const timeStamp = new Date().getTime()
  const prefix = path.includes('?') ? '&' : '?'
  return `${path}${prefix}timeStamp=${timeStamp}`
}

export default class DXPService {
  static async fetchCall({ url, method, data }: RequestConfig): Promise<Response> {
    const connectionString = import.meta.env.VITE_KEY_INSIGHTS
    const path = `${getDXPEnvironmentRootPath()}${url}`
    const httpMeta = createHttpMeta({
      method: method ?? 'GET',
      url: path,
      request: data
    })

    try {
      const config = {
        method,
        body: JSON.stringify(data)
      }
      const responseJSON = await fetch(clearCache(path), config)
      const response = await responseJSON.json()

      if (!responseJSON.ok) {
        const error = new ServiceError({ code: responseJSON.status })

        if (connectionString) {
          trackHttpError(connectionString, httpMeta, error, {
            trackException: false,
            properties: {
              serviceName: url,
              serviceType: 'DXP'
            }
          })
        }

        throw error
      }

      if (connectionString) {
        trackHttpResponse(
          connectionString,
          httpMeta,
          { status: responseJSON.status, data: response },
          {
            properties: {
              serviceName: url,
              serviceType: 'DXP'
            }
          }
        )
      }

      return new Response({ data: response })
    } catch (error: any) {
      const isAborted = error.name === 'AbortError'
      const serviceError = new ServiceError({
        ...error,
        code: error.code,
        serviceName: url
      })

      if (!isAborted && connectionString) {
        trackHttpError(connectionString, httpMeta, serviceError, {
          trackException: true,
          properties: {
            serviceName: url,
            serviceType: 'DXP',
            isAborted
          }
        })
      }

      return new Response({ data: serviceError, error: true, aborted: isAborted })
    }
  }

  static async get(url: string, config?: RequestConfig) {
    return this.fetchCall({ ...config, url, method: 'GET' })
  }

  static async post(url: string, data: unknown, config?: RequestConfig) {
    return this.fetchCall({ ...config, url, method: 'POST', data })
  }
}
